import { getCustomerSignOnLink } from "@dillon/Services/InvoicePortalService";
import { mediaQueriesBP } from "@dillon/Utilities/variables";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Button from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import styled, { css } from "styled-components";

/** Must match NAVIGATING_AWAY_WINDOW_KEY in client-framework HandleError. */
const NAVIGATING_AWAY_WINDOW_KEY = "__insiteNavigatingAway";

export type InvoicePortalButtonPlacement = "header" | "accountMobile" | "headerMobile" | "invoiceHistory";

export interface InvoicePortalButtonCoreProps {
    variant: "primary" | "secondary" | "tertiary";
    label: string;
    alignment: string;
    customCSS?: string;
    placement: InvoicePortalButtonPlacement;
    isAuthenticated: boolean;
    onNavigate?: () => void;
}

export interface InvoicePortalButtonStyles {
    wrapperStyles: InjectableCss;
}

export const InvoicePortalButtonContentWrapper = styled.div<
    React.HTMLAttributes<HTMLDivElement> &
        InjectableCss & {
            $alignment: string;
            $placement: InvoicePortalButtonPlacement;
            $isInShell: boolean;
        }
>`
    display: none;
    box-sizing: border-box;
    width: ${({ $placement }) => ($placement === "headerMobile" || $placement === "header" ? "auto" : "100%")};
    max-width: ${({ $placement }) => ($placement === "headerMobile" || $placement === "header" ? "none" : "100%")};
    min-width: ${({ $placement }) => ($placement === "headerMobile" || $placement === "header" ? "0" : "100%")};
    align-self: center;

    ${({ $isInShell, $placement }) =>
        $isInShell &&
        $placement === "header" &&
        css`
            display: flex;
            align-items: center;
            height: auto;

            .button {
                height: auto;
                align-self: center;
            }
        `}

    ${({ $isInShell, $placement }) =>
        $isInShell &&
        $placement === "headerMobile" &&
        css`
            /* Preview mobile button only at mobile widths — avoids duplicate on desktop */
            ${mediaQueriesBP.maxMD} {
                display: flex;
                align-items: center;
                padding: 0 8px;
            }

            ${mediaQueriesBP.minMD} {
                display: none !important;
            }

            .button {
                white-space: nowrap;
                padding-inline: 12px;
                min-height: 32px;
                font-size: 12px;
            }
        `}

    ${({ $isInShell, $placement }) =>
        $isInShell &&
        $placement === "accountMobile" &&
        css`
            display: flex;
            margin-block: 20px;
        `}

    ${({ $isInShell, $placement }) =>
        $isInShell &&
        $placement === "invoiceHistory" &&
        css`
            display: flex;
            margin-block: 0 20px;

            .button {
                width: 100%;
            }
        `}

    ${({ $isInShell, $placement }) =>
        !$isInShell &&
        $placement === "accountMobile" &&
        css`
            ${mediaQueriesBP.maxMD} {
                display: flex;
            }

            .button {
                width: 100%;
            }
        `}

    ${({ $isInShell, $placement }) =>
        !$isInShell &&
        $placement === "headerMobile" &&
        css`
            ${mediaQueriesBP.maxMD} {
                display: flex;
                align-items: center;
                padding: 0 8px;
            }

            ${mediaQueriesBP.minMD} {
                display: none !important;
            }

            .button {
                white-space: nowrap;
                padding-inline: 12px;
                min-height: 32px;
                font-size: 12px;
            }
        `}

    ${({ $isInShell, $placement }) =>
        !$isInShell &&
        $placement === "invoiceHistory" &&
        css`
            ${mediaQueriesBP.maxMD} {
                display: flex;
                margin-block: 0 20px;
            }

            .button {
                width: 100%;
            }
        `}

    ${({ $isInShell, $placement }) =>
        !$isInShell &&
        $placement === "header" &&
        css`
            ${mediaQueriesBP.minMD} {
                display: flex;
                align-items: center;
                height: auto;
            }

            .button {
                height: auto;
                align-self: center;
            }
        `}

    ${injectCss};

    justify-content: ${({ $alignment }) => $alignment || "center"};
`;

export const InvoicePortalButtonCore: FC<InvoicePortalButtonCoreProps> = ({
    variant,
    label,
    alignment,
    customCSS,
    placement,
    isAuthenticated,
    onNavigate,
}) => {
    const { isInShell } = useShellContext();
    const [signOnLink, setSignOnLink] = useState<string>();
    const resolvedAlignment = alignment || "center";

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        getCustomerSignOnLink()
            .then(result => {
                if (result?.SignOnLink) {
                    setSignOnLink(result.SignOnLink);
                }
            })
            .catch(() => {
                setSignOnLink(undefined);
            });
    }, [isAuthenticated]);

    const onClick = () => {
        if (!signOnLink) {
            return;
        }

        if (isInShell) {
            return;
        }

        onNavigate?.();
        // Mark intentional navigation so Safari/WebKit aborted fetches do not open the unhandled-error modal
        // during the (often multi-second) unload before the SSO destination paints.
        (window as Window & { [NAVIGATING_AWAY_WINDOW_KEY]?: boolean })[NAVIGATING_AWAY_WINDOW_KEY] = true;
        window.location.assign(signOnLink);
    };

    if (!isAuthenticated) {
        return null;
    }

    if (!isInShell && !signOnLink) {
        return null;
    }

    return (
        <InvoicePortalButtonContentWrapper
            $alignment={resolvedAlignment}
            $placement={placement}
            $isInShell={!!isInShell}
            data-placement={placement}
            className="wrapper InvoicePortalButton"
            style={{ justifyContent: resolvedAlignment }}
            css={css`
                ${customCSS}
            `}
        >
            <Button variant={variant} onClick={onClick} className="button">
                {label}
            </Button>
        </InvoicePortalButtonContentWrapper>
    );
};
